import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { addressFormSchema } from '../addess/schema';

export const addOfficeFormSchema = z
  .object({
    name: z
      .string({ message: 'form.errors.required' })
      .min(1, { message: 'form.errors.required' }),
    siteId: z
      .string({ message: 'form.errors.required' })
      .min(1, { message: 'form.errors.required' }),
    nationalMedicalOrderMail: z
      .string({ message: 'form.errors.required' })
      .email({ message: 'form.errors.invalid-email' }),
    estimatedCaPerSlot: z.coerce
      .number({
        message: 'form.errors.required',
      })
      .refine(
        (value) => {
          return !Number.isNaN(Number(value)) && value >= 0;
        },
        {
          message: 'form.errors.required',
        },
      ),
    averageSlotHonorary: z.coerce
      .number({
        message: 'form.errors.required',
      })
      .min(0, { message: 'form.errors.required' })
      .default(0),
    withAssistantRetrocessionRate: z.coerce
      .number({ message: 'form.errors.required' })
      .gt(0, { message: 'form.errors.required' })
      .max(1),
    withoutAssistantRetrocessionRate: z.coerce
      .number({ message: 'form.errors.required' })
      .gt(0, { message: 'form.errors.required' })
      .max(1),
    weekendRetrocessionRate: z.coerce
      .number({ message: 'form.errors.required' })
      .gt(0, { message: 'form.errors.required' })
      .max(1),
  })
  .merge(addressFormSchema);

export type AddOfficeFormValues = z.infer<typeof addOfficeFormSchema>;

export const addOfficeFormResolver = zodResolver(addOfficeFormSchema);

export const editOfficeFormSchema = addOfficeFormSchema
  .omit({ siteId: true })
  .extend({
    /**
     * Whether the practice is already open. Display-only: drives the
     * recruitment page's "· bientôt" chip and nothing else.
     *
     * Only on the edit schema — a new office is open unless someone says
     * otherwise, and `PUT /offices/:id` is a full replace, so every card that
     * saves an office has to pass the current value through or it would reset.
     */
    open: z.boolean(),
  });

export type EditOfficeFormValues = z.infer<typeof editOfficeFormSchema>;

export const editOfficeFormResolver = zodResolver(editOfficeFormSchema);

export const editOfficeGeneralInfoSchema = editOfficeFormSchema
  .pick({
    name: true,
    nationalMedicalOrderMail: true,
    address: true,
    open: true,
  })
  .extend({
    // User id of the assigned CSM, or null when unassigned
    assignedCsmId: z.string().nullable().default(null),
  });

export type EditOfficeGeneralInfoValues = z.infer<
  typeof editOfficeGeneralInfoSchema
>;

export const editOfficeGeneralInfoResolver = zodResolver(
  editOfficeGeneralInfoSchema,
);

export const editOfficeFinancialsSchema = editOfficeFormSchema.pick({
  estimatedCaPerSlot: true,
  averageSlotHonorary: true,
  withAssistantRetrocessionRate: true,
  withoutAssistantRetrocessionRate: true,
  weekendRetrocessionRate: true,
});

export type EditOfficeFinancialsValues = z.infer<
  typeof editOfficeFinancialsSchema
>;

export const editOfficeFinancialsResolver = zodResolver(
  editOfficeFinancialsSchema,
);
